import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "General inquiry",
  "ElderlyAssist",
  "Partnerships",
  "Press",
  "Careers",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    interest: "General inquiry",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        interest: form.interest,
      });
      if (res.data?.success) {
        setSubmitted(true);
        toast.success("Message received. We'll be in touch.");
        setForm({
          name: "",
          email: "",
          company: "",
          message: "",
          interest: "General inquiry",
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 bg-[#F2EBE5] grain"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="text-xs tracking-[0.24em] uppercase text-sentivo-secondary mb-6">
            Contact
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sentivo-ink text-balance">
            Let's build
            <span className="italic text-sentivo-accent"> kinder </span>
            care, together.
          </h2>
          <p className="mt-6 text-lg text-sentivo-muted leading-relaxed">
            Whether you're a clinician, family, partner or investor — we'd love to hear
            what you're working on.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-sentivo-accent border border-sentivo-line">
                <Mail size={16} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-sentivo-secondary">
                  Email
                </div>
                <a
                  href="mailto:hello@sentivotech.ai"
                  data-testid="contact-email-link"
                  className="font-serif text-xl text-sentivo-ink hover:text-sentivo-accent transition-colors"
                >
                  hello@sentivotech.ai
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FORM */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl bg-white border border-sentivo-line p-8 md:p-12 shadow-sm">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
                data-testid="contact-success-state"
              >
                <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-5" />
                <h3 className="font-serif text-3xl text-sentivo-ink mb-3">
                  Message received.
                </h3>
                <p className="text-sentivo-muted">
                  Thank you for reaching out — our team will respond within two business days.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-sentivo-line px-5 py-2.5 text-sm font-medium text-sentivo-primary hover:bg-sentivo-surface transition"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Your name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    required
                    testId="contact-input-name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                    required
                    testId="contact-input-email"
                  />
                </div>

                <Field
                  label="Company / Organisation"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Optional"
                  testId="contact-input-company"
                />

                <div>
                  <label className="block text-xs uppercase tracking-widest text-sentivo-secondary mb-2">
                    I'm interested in
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="contact-interest-group">
                    {INTERESTS.map((i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setForm({ ...form, interest: i })}
                        data-testid={`contact-interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-4 py-2 text-sm rounded-full border transition-all ${
                          form.interest === i
                            ? "bg-sentivo-primary text-white border-sentivo-primary"
                            : "bg-white text-sentivo-primary border-sentivo-line hover:border-sentivo-secondary"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-sentivo-secondary mb-2">
                    Message
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a little about what you're hoping to explore."
                    required
                    data-testid="contact-input-message"
                    className="w-full rounded-2xl border border-sentivo-line bg-[#FAF9F6] px-5 py-4 text-sentivo-ink placeholder:text-sentivo-muted/70 focus:outline-none focus:border-sentivo-accent focus:ring-2 focus:ring-sentivo-accent/20 transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-sentivo-accent hover:bg-sentivo-accentHover text-white px-7 py-4 text-sm font-semibold tracking-wide transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  {loading ? "Sending..." : "Send message"}
                  <Send size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text", required, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-sentivo-secondary mb-2">
      {label}
      {required && <span className="text-sentivo-accent ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testId}
      className="w-full rounded-2xl border border-sentivo-line bg-[#FAF9F6] px-5 py-4 text-sentivo-ink placeholder:text-sentivo-muted/70 focus:outline-none focus:border-sentivo-accent focus:ring-2 focus:ring-sentivo-accent/20 transition"
    />
  </div>
);
